import React, { useState, useEffect } from 'react';
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { auth } from '../../firebase';
import { useNavigate, Link } from 'react-router-dom';
import background from '../../assets/Background_Test.jpg';

const styles = {
  page: {
    minHeight: '100vh',
    backgroundImage: 'url(' + background + ')',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'Monda', sans-serif",
    position: 'relative',
  },
  overlay: {
    position: 'absolute', inset: 0,
    background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.75) 100%)',
    pointerEvents: 'none',
  },
  title: {
    fontFamily: "'Anton', sans-serif",
    fontSize: '28px', letterSpacing: '3px',
    color: '#fff', textAlign: 'center', marginBottom: '8px',
    textShadow: '0 0 30px rgba(200,0,0,0.4)',
  },
  card: {
    position: 'relative', zIndex: 2,
    background: 'rgba(10,10,10,0.82)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '12px', padding: '40px',
    width: '100%', maxWidth: '420px',
    boxShadow: '0 8px 60px rgba(0,0,0,0.8), inset 0 1px 0 rgba(255,255,255,0.05)',
    display: 'flex', flexDirection: 'column', gap: '16px',
  },
  label: { color: 'rgba(255,255,255,0.5)', fontSize: '11px', letterSpacing: '1px', fontFamily: "'Monda', sans-serif", marginBottom: '4px' },
  emailChip: {
    color: 'rgba(212,175,55,0.9)', fontSize: '13px', fontFamily: "'Monda', sans-serif",
    background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.25)',
    borderRadius: '4px', padding: '8px 12px', textAlign: 'center',
  },
  input: {
    width: '100%', background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '4px', padding: '10px 12px',
    color: '#fff', fontSize: '14px', outline: 'none',
    fontFamily: "'Monda', sans-serif", transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  },
  btn: {
    fontFamily: "'Monda', sans-serif", letterSpacing: '1px',
    background: 'transparent', border: '1px solid rgba(255,255,255,0.5)',
    color: '#fff', padding: '10px', borderRadius: '4px',
    cursor: 'pointer', fontSize: '12px', transition: 'background 0.2s, border-color 0.2s, color 0.2s', width: '100%',
  },
  error: { color: '#ff4444', fontSize: '12px', fontFamily: "'Monda', sans-serif" },
  success: { color: '#2ecc71', fontSize: '13px', fontFamily: "'Monda', sans-serif", textAlign: 'center', lineHeight: 1.6 },
  link: { color: 'rgba(255,255,255,0.4)', fontSize: '12px', textAlign: 'center', fontFamily: "'Monda', sans-serif" },
  loading: { color: 'rgba(255,255,255,0.4)', fontSize: '12px', fontFamily: "'Monda', sans-serif", textAlign: 'center' },
};

// ── Statuts possibles ────────────────────────────────────────────────
// verifying   -> on vérifie l'oobCode auprès de Firebase
// ready       -> code valide, formulaire affiché
// invalid     -> code invalide, expiré, ou déjà utilisé
// success     -> mot de passe changé avec succès
export default function ResetPassword() {
  const [status, setStatus] = useState('verifying');
  const [email, setEmail] = useState('');
  const [oobCode, setOobCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [hovered, setHovered] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('oobCode');
    const mode = params.get('mode');

    if (!code || mode !== 'resetPassword') {
      setStatus('invalid');
      return;
    }
    setOobCode(code);

    verifyPasswordResetCode(auth, code)
      .then((verifiedEmail) => {
        setEmail(verifiedEmail);
        setStatus('ready');
      })
      .catch(() => setStatus('invalid'));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setStatus('success');
    } catch (err) {
      if (err.code === 'auth/expired-action-code' || err.code === 'auth/invalid-action-code') {
        setStatus('invalid');
      } else if (err.code === 'auth/weak-password') {
        setError('Password too short (6 characters min).');
      } else {
        setError('Something went wrong: ' + err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <link href="https://fonts.googleapis.com/css2?family=Anton&family=Imprima&family=Monda:wght@400;700&display=swap" rel="stylesheet" />
      <div style={styles.page}>
        <div style={styles.overlay} />
        <div style={{ position: 'relative', zIndex: 2, width: '100%', maxWidth: '420px', padding: '0 16px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <img
            src={process.env.PUBLIC_URL + '/logo-main.png'}
            alt="Logo"
            style={{ width: '160px', height: '160px', objectFit: 'contain', filter: 'drop-shadow(0 0 20px rgba(255,255,255,0.15))', marginBottom: '8px' }}
            onError={function(e) { e.target.style.display = 'none'; }}
          />
          <div style={styles.title}>RESET PASSWORD</div>
          <div style={styles.card}>

            {status === 'verifying' && (
              <div style={styles.loading}>Verifying your reset link...</div>
            )}

            {status === 'invalid' && (
              <>
                <div style={styles.error}>
                  This password reset link is invalid or has expired. Reset links are only valid for a limited time and can only be used once.
                </div>
                <Link to="/login" style={{ textDecoration: 'none' }}>
                  <button style={{ ...styles.btn, background: hovered ? 'rgba(255,255,255,0.1)' : 'transparent', borderColor: hovered ? '#fff' : 'rgba(255,255,255,0.5)' }}
                    onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
                    BACK TO LOGIN
                  </button>
                </Link>
              </>
            )}

            {status === 'ready' && (
              <>
                <div style={styles.emailChip}>{email}</div>
                <div>
                  <div style={styles.label}>NEW PASSWORD</div>
                  <input style={styles.input} type="password" value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.5)'; }}
                    onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.15)'; }} />
                </div>
                <div>
                  <div style={styles.label}>CONFIRM NEW PASSWORD</div>
                  <input style={styles.input} type="password" value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onFocus={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.5)'; }}
                    onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.15)'; }} />
                </div>
                {error && <div style={styles.error}>{error}</div>}
                <button
                  style={{ ...styles.btn, background: hovered ? 'rgba(255,255,255,0.1)' : 'transparent', borderColor: hovered ? '#fff' : 'rgba(255,255,255,0.5)', opacity: submitting ? 0.6 : 1 }}
                  onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
                  onClick={handleSubmit} disabled={submitting}>
                  {submitting ? 'UPDATING...' : 'UPDATE PASSWORD'}
                </button>
              </>
            )}

            {status === 'success' && (
              <>
                <div style={styles.success}>
                  Your password has been updated successfully.<br />You can now log in with your new password.
                </div>
                <button
                  style={{ ...styles.btn, background: hovered ? 'rgba(255,255,255,0.1)' : 'transparent', borderColor: hovered ? '#fff' : 'rgba(255,255,255,0.5)' }}
                  onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
                  onClick={() => navigate('/login')}>
                  GO TO LOGIN
                </button>
              </>
            )}

            {status !== 'success' && status !== 'invalid' && (
              <div style={styles.link}>
                <Link to="/login" style={{ color: 'rgba(255,255,255,0.7)' }}>Back to login</Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
